/**
 * src/services/documents.api.ts 의 짝.
 *
 * 폴더·열·선택지·문서를 만들고 고치고 지우는 것은 로그인한 전원이다 — backend ADR
 * `2026-10-30-let-everyone-own-the-wiki.md`. 지운 폴더와 문서는 휴지통으로 간다.
 *
 * 충돌 규칙도 여기서 흉내 낸다 — 칸(제목·열 값)은 나중에 저장한 쪽이 이기고, 본문(body_md)은
 * 읽은 version 이 지금과 다르면 409 로 거절한다. 긴 글만 덮어쓰면 복구할 수 없기 때문이다.
 */
import { HttpResponse, http } from "msw";
import type {
  DocumentField,
  DocumentFieldInput,
  DocumentFieldPatch,
  DocumentOption,
  DocumentOptionInput,
  DocumentOptionPatch,
  DocumentDetail,
  DocumentInput,
  DocumentPatch,
  DocumentFolderInput,
  DocumentFolderPatch,
} from "@/types";
import { db } from "../db";
import { fail, latency, requireUser } from "../http";

const FOLDERS = "/api/v1/document-folders";
const FIELDS = "/api/v1/document-fields";
const DOCS = "/api/v1/documents";
const w = db.documents;
const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;
let nextId = 1000;
const newId = (prefix: string) => `${prefix}${nextId++}`;

async function guard() {
  await latency();
  const user = requireUser();
  return user instanceof Response ? { denied: user } : { user };
}

/** 본문 앞부분 — 마크다운 기호를 걷고 한 줄로. */
function excerptOf(md: string) {
  return md
    .replace(/^#+\s*/gm, "")
    .replace(/^\s*[-*]\s+(\[[ x]\]\s*)?/gm, "")
    .replace(/[*_`>~]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 140);
}

const summary = ({ body_md: body, version: _version, ...row }: DocumentDetail) => ({
  ...row,
  excerpt: excerptOf(body),
});
const full = (row: DocumentDetail) => ({ ...row, excerpt: excerptOf(row.body_md) });

const folderOf = (id: string) => w.folders.find((folder) => folder.id === id);
const rowOf = (id: string) => w.documents.find((row) => row.id === id);
const fieldOf = (fieldId: string) =>
  w.folders
    .flatMap((folder) => folder.fields.map((field) => ({ folder, field })))
    .find((pair) => pair.field.id === fieldId);

const sortedFolders = (includeInactive: boolean) =>
  w.folders
    .filter((folder) => includeInactive || folder.is_active)
    .sort((a, b) => a.position - b.position)
    .map((folder) => ({
      ...folder,
      fields: [...folder.fields]
        .sort((a, b) => a.position - b.position)
        .map((field) => ({
          ...field,
          options: [...field.options].sort((a, b) => a.position - b.position),
        })),
    }));

/** 선택지를 지우면 그것을 고른 값이 비워진다. */
function dropOptionValues(folderId: string, field: DocumentField, optionId: string) {
  for (const row of w.documents.filter((item) => item.folder_id === folderId)) {
    const value = row.values[field.id];
    if (typeof value === "string" && value === optionId) delete row.values[field.id];
    if (Array.isArray(value)) {
      const left = value.filter((id) => id !== optionId);
      if (left.length) row.values[field.id] = left;
      else delete row.values[field.id];
    }
  }
}

export const documentsHandlers = [
  http.get(`${FOLDERS}/`, async ({ request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const includeInactive = new URL(request.url).searchParams.get("include_inactive") === "true";
    return HttpResponse.json({ results: sortedFolders(includeInactive) });
  }),

  http.post(`${FOLDERS}/`, async ({ request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const { slug, name } = (await request.json()) as DocumentFolderInput;
    if (!name?.trim()) return fail(422, "문서 폴더 이름을 입력하세요.");
    if (!slug || !SLUG.test(slug)) return fail(422, "주소 이름은 영문 소문자와 하이픈이에요.");
    if (w.folders.some((folder) => folder.slug === slug))
      return fail(409, "같은 주소 이름이 있어요.");
    const folder = {
      id: newId("t-"),
      slug,
      name: name.trim(),
      icon: "",
      color: "",
      description: "",
      position: Math.max(0, ...w.folders.map((item) => item.position)) + 10,
      is_active: true,
      fields: [],
    };
    w.folders.push(folder);
    return HttpResponse.json(folder, { status: 201 });
  }),

  http.patch(`${FOLDERS}/:id/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    if (!folder) return fail(404, "없는 문서 폴더예요.");
    const patch = (await request.json()) as DocumentFolderPatch;
    if (patch.name !== undefined) {
      if (!patch.name.trim()) return fail(422, "문서 폴더 이름을 입력하세요.");
      folder.name = patch.name.trim();
    }
    if (patch.slug !== undefined) {
      if (!SLUG.test(patch.slug)) return fail(422, "주소 이름은 영문 소문자와 하이픈이에요.");
      if (w.folders.some((item) => item.slug === patch.slug && item.id !== folder.id))
        return fail(409, "같은 주소 이름이 있어요.");
      folder.slug = patch.slug;
    }
    if (patch.icon !== undefined) folder.icon = patch.icon;
    if (patch.color !== undefined) folder.color = patch.color;
    if (patch.description !== undefined) folder.description = patch.description;
    if (patch.position !== undefined) folder.position = patch.position;
    if (patch.is_active !== undefined) folder.is_active = patch.is_active;
    return HttpResponse.json(folder);
  }),

  http.get(`${FOLDERS}/:id/delete-preview/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    if (!folderOf(String(params.id))) return fail(404, "없는 문서 폴더예요.");
    // 서버처럼 휴지통에 없는 문서만 센다 — 함께 휴지통으로 갈 문서 수다.
    const documentCount = w.documents.filter(
      (doc) => doc.folder_id === params.id && !doc.deleted_at
    ).length;
    return HttpResponse.json({ document_count: documentCount });
  }),

  http.delete(`${FOLDERS}/:id/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const id = String(params.id);
    if (!folderOf(id)) return fail(404, "없는 문서 폴더예요.");
    // 서버처럼 폴더는 목록에서 빼고 그 안의 문서는 휴지통으로 보낸다(삭제 시각만 채운다).
    w.folders = w.folders.filter((folder) => folder.id !== id);
    const stamp = new Date().toISOString();
    for (const doc of w.documents)
      if (doc.folder_id === id && !doc.deleted_at) doc.deleted_at = stamp;
    return new HttpResponse(null, { status: 204 });
  }),

  http.post(`${FOLDERS}/:id/fields/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    if (!folder) return fail(404, "없는 문서 폴더예요.");
    const { name, value_type: valueType } = (await request.json()) as Partial<DocumentFieldInput>;
    if (!name?.trim()) return fail(422, "열 이름을 입력하세요.");
    if (!valueType) return fail(422, "열 종류를 고르세요.");
    const field: DocumentField = {
      id: newId("pr-"),
      name: name.trim(),
      value_type: valueType,
      position: Math.max(0, ...folder.fields.map((item) => item.position)) + 10,
      is_active: true,
      options: [],
    };
    folder.fields.push(field);
    return HttpResponse.json(field, { status: 201 });
  }),

  http.patch(`${FOLDERS}/:id/fields/:fieldId/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    const field = folder?.fields.find((item) => item.id === params.fieldId);
    if (!folder || !field) return fail(404, "없는 열이에요.");
    const patch = (await request.json()) as DocumentFieldPatch & { value_type?: unknown };
    if (patch.value_type !== undefined) return fail(422, "열의 종류는 만든 뒤 바꿀 수 없어요.");
    if (patch.name !== undefined) {
      if (!patch.name.trim()) return fail(422, "열 이름을 입력하세요.");
      field.name = patch.name.trim();
    }
    if (patch.position !== undefined) field.position = patch.position;
    if (patch.is_active !== undefined) field.is_active = patch.is_active;
    return HttpResponse.json(field);
  }),

  http.delete(`${FOLDERS}/:id/fields/:fieldId/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    if (!folder) return fail(404, "없는 문서 폴더예요.");
    const fieldId = String(params.fieldId);
    folder.fields = folder.fields.filter((item) => item.id !== fieldId);
    for (const row of w.documents.filter((item) => item.folder_id === folder.id))
      delete row.values[fieldId];
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 선택지 — 하나씩 더하고 고친다 ───────────────────────────────── */

  http.post(`${FIELDS}/:fieldId/options/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const found = fieldOf(String(params.fieldId));
    if (!found) return fail(404, "없는 열이에요.");
    const { field } = found;
    if (field.value_type !== "select" && field.value_type !== "multi_select")
      return fail(422, "선택지를 둘 수 있는 열이 아니에요.");
    const { name, color = "gray" } = (await request.json()) as DocumentOptionInput;
    if (!name?.trim()) return fail(422, "선택지 이름을 입력하세요.");
    const option: DocumentOption = {
      id: newId("o-"),
      name: name.trim(),
      color,
      position: Math.max(0, ...field.options.map((item) => item.position)) + 10,
    };
    field.options.push(option);
    return HttpResponse.json(option, { status: 201 });
  }),

  http.patch(`${FIELDS}/:fieldId/options/:optionId/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const found = fieldOf(String(params.fieldId));
    const option = found?.field.options.find((item) => item.id === params.optionId);
    if (!option) return fail(404, "없는 선택지예요.");
    const patch = (await request.json()) as DocumentOptionPatch;
    if (patch.name !== undefined) {
      if (!patch.name.trim()) return fail(422, "선택지 이름을 입력하세요.");
      option.name = patch.name.trim();
    }
    if (patch.color !== undefined) option.color = patch.color;
    if (patch.position !== undefined) option.position = patch.position;
    return HttpResponse.json(option);
  }),

  http.delete(`${FIELDS}/:fieldId/options/:optionId/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const found = fieldOf(String(params.fieldId));
    if (!found) return fail(404, "없는 열이에요.");
    const optionId = String(params.optionId);
    found.field.options = found.field.options.filter((item) => item.id !== optionId);
    dropOptionValues(found.folder.id, found.field, optionId);
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 문서 ────────────────────────────────────────────────────────── */

  http.get(`${FOLDERS}/:id/documents/`, async ({ params, request }) => {
    const { denied } = await guard();
    if (denied) return denied;
    if (!folderOf(String(params.id))) return fail(404, "없는 문서 폴더예요.");
    const query = new URL(request.url).searchParams;
    const projectId = query.get("project_id");
    const from = query.get("period_from");
    const to = query.get("period_to");
    const keyword = (query.get("keyword") ?? "").trim().toLowerCase();
    const results = w.documents
      .filter((row) => row.folder_id === params.id && !row.deleted_at)
      .filter((row) => !projectId || row.project_id === projectId)
      .filter((row) => !from || row.created_at.slice(0, 10) >= from)
      .filter((row) => !to || row.created_at.slice(0, 10) <= to)
      .filter(
        (row) =>
          !keyword ||
          row.title.toLowerCase().includes(keyword) ||
          row.body_md.toLowerCase().includes(keyword)
      )
      .sort((a, b) => a.position - b.position)
      .map(summary);
    return HttpResponse.json({ results });
  }),

  http.get(`${FOLDERS}/:id/trash/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    if (!folderOf(String(params.id))) return fail(404, "없는 문서 폴더예요.");
    const results = w.documents
      .filter((row) => row.folder_id === params.id && row.deleted_at)
      .sort((a, b) => (a.deleted_at! < b.deleted_at! ? 1 : -1))
      .map(summary);
    return HttpResponse.json({ results });
  }),

  http.post(`${FOLDERS}/:id/documents/`, async ({ params, request }) => {
    const { denied, user } = await guard();
    if (denied) return denied;
    const folder = folderOf(String(params.id));
    if (!folder) return fail(404, "없는 문서 폴더예요.");
    const input = (await request.json()) as DocumentInput;
    const stamp = new Date().toISOString();
    const row: DocumentDetail = {
      id: newId("r-"),
      folder_id: folder.id,
      title: input.title?.trim() ?? "",
      excerpt: "",
      project_id: input.project_id ?? null,
      position: Math.max(0, ...w.documents.map((item) => item.position)) + 10,
      values: input.values ?? {},
      body_md: input.body_md ?? "",
      version: 1,
      created_by: user.id,
      created_at: stamp,
      updated_by: user.id,
      updated_at: stamp,
      deleted_at: null,
    };
    w.documents.push(row);
    return HttpResponse.json(full(row), { status: 201 });
  }),

  http.get(`${DOCS}/:id/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const row = rowOf(String(params.id));
    return row ? HttpResponse.json(full(row)) : fail(404, "없는 문서예요.");
  }),

  http.patch(`${DOCS}/:id/`, async ({ params, request }) => {
    const { denied, user } = await guard();
    if (denied) return denied;
    const row = rowOf(String(params.id));
    if (!row) return fail(404, "없는 문서예요.");
    const patch = (await request.json()) as DocumentPatch;
    if (patch.body_md !== undefined) {
      if (patch.version !== row.version)
        return fail(409, "다른 사람이 먼저 본문을 고쳤어요. 최신 내용을 불러올게요.");
      row.body_md = patch.body_md;
      row.version += 1;
    }
    if (patch.title !== undefined) row.title = patch.title.trim();
    if (patch.project_id !== undefined) row.project_id = patch.project_id;
    for (const [key, value] of Object.entries(patch.values ?? {})) {
      if (value === null || (Array.isArray(value) && value.length === 0)) delete row.values[key];
      else row.values[key] = value;
    }
    row.updated_at = new Date().toISOString();
    row.updated_by = user.id;
    return HttpResponse.json(full(row));
  }),

  http.delete(`${DOCS}/:id/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const row = rowOf(String(params.id));
    if (!row) return fail(404, "없는 문서예요.");
    // 휴지통으로 — 행은 지우지 않고 삭제 시각만 채운다.
    row.deleted_at = new Date().toISOString();
    return new HttpResponse(null, { status: 204 });
  }),

  http.post(`${DOCS}/:id/restore/`, async ({ params }) => {
    const { denied } = await guard();
    if (denied) return denied;
    const row = rowOf(String(params.id));
    if (!row) return fail(404, "없는 문서예요.");
    row.deleted_at = null;
    return HttpResponse.json(full(row));
  }),
];
